import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ApiResponse, User } from '../types';

// Configuración base de la API
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://erp-captaincode-backend-test-329964b19474.herokuapp.com/api';

// Crear instancia de axios
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Interceptor para agregar token de autenticación y headers correctos
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Ensure correct Content-Type for JSON requests
    if (config.data && typeof config.data === 'object') {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// ── Refresh token logic ────────────────────────────────────────────────────

// Guard para evitar múltiples refreshes simultáneos (cola de requests en espera).
let isRefreshing = false;
let pendingQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  pendingQueue = [];
}

// Guard para evitar múltiples redirects simultáneos tras un refresh fallido.
let isRedirecting = false;

const forceLogout = () => {
  if (isRedirecting) return;
  isRedirecting = true;
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.location.href = '/login?expired=true';
};

// ── Response interceptor ───────────────────────────────────────────────────

// Tipo extendido para marcar requests que ya fueron reintentadas.
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalConfig = error.config as RetryableConfig;

    if (error.response?.status === 401) {
      // Endpoints que NO deben disparar el flujo de refresh:
      //   - login: un 401 significa credenciales incorrectas
      //   - refresh: si el refresh falla, ya no hay nada que hacer → logout
      const isAuthBypass =
        originalConfig?.url?.includes('/auth/login') ||
        originalConfig?.url?.includes('/auth/refresh');

      if (!isAuthBypass && !originalConfig._retry) {
        // Primera vez que falla esta request → intentar renovar el token.
        if (isRefreshing) {
          // Ya hay un refresh en curso: encolar esta request para reintentarla
          // cuando el refresh termine.
          return new Promise((resolve, reject) => {
            pendingQueue.push({
              resolve: (newToken) => {
                originalConfig.headers.Authorization = `Bearer ${newToken}`;
                resolve(api(originalConfig));
              },
              reject,
            });
          });
        }

        originalConfig._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          // No hay refresh token → logout directo.
          isRefreshing = false;
          processQueue(error, null);
          forceLogout();
          return Promise.reject(error);
        }

        try {
          // Llamada directa con axios base para evitar que el interceptor
          // intercepte el propio request de refresh y genere un loop infinito.
          const { data } = await axios.post<ApiResponse<{ token: string; refreshToken: string }>>(
            `${API_BASE_URL}/auth/refresh`,
            { refreshToken },
          );

          if (!data.success || !data.data?.token) {
            throw new Error('Refresh response inválida');
          }

          const newToken = data.data.token;
          const newRefreshToken = data.data.refreshToken;
          const refreshedUser = (data.data as any).user as User | undefined;

          // Persistir los nuevos tokens
          localStorage.setItem('token', newToken);
          if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);

          // Si el backend devolvió el perfil actualizado, sincronizar localStorage
          // y notificar a AuthContext para que actualice permisos en vivo.
          if (refreshedUser) {
            localStorage.setItem('user', JSON.stringify(refreshedUser));
            window.dispatchEvent(new CustomEvent('auth:permissions-updated', { detail: refreshedUser }));
          }

          // Notificar a todas las requests en cola y desbloquear
          processQueue(null, newToken);

          // Reintentar la request original con el nuevo token
          originalConfig.headers.Authorization = `Bearer ${newToken}`;
          return api(originalConfig);
        } catch (refreshError) {
          // Refresh fallido (token expirado o revocado) → logout
          processQueue(refreshError, null);
          forceLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Si ya fue reintentada o es un bypass → logout directo
      if (!isAuthBypass) {
        forceLogout();
      }
    }

    if (error.response?.status === 403) {
      // Si no hay token, es un problema de autenticación, no de permisos
      const token = localStorage.getItem('token');
      if (!token) {
        forceLogout();
      }
      // Si hay token, es un error de permisos: dejar que el componente lo maneje
    }

    return Promise.reject(error);
  },
);

// ── Helpers ────────────────────────────────────────────────────────────────

export const handleApiResponse = <T>(
  response: AxiosResponse<ApiResponse<T>>,
): T => {
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || 'Error en la operación');
};

export default api;
